// frontend/src/components/ManualFaceCapture.jsx
import React, { useState, useRef, useEffect } from 'react';
import { CameraIcon, CheckIcon, XIcon, RefreshIcon, ExclamationIcon } from '@heroicons/react/outline';
import api from '../services/api';
import { showSuccess } from '../utils/toastUtils';
import { getTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext';

const ManualFaceCapture = ({
    accessToken,
    onCaptureComplete,
    photoOrder = 1
}) => {
    const [loading, setLoading] = useState(false);
    const [captured, setCaptured] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [error, setError] = useState(null);
    const [cameraReady, setCameraReady] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [isCountingDown, setIsCountingDown] = useState(false);
    // ⭐ NEW: track permission state
    const [permissionState, setPermissionState] = useState('prompt'); // 'prompt' | 'granted' | 'denied' | 'unavailable'
    const [permissionChecked, setPermissionChecked] = useState(false);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const { language } = useLanguage();
    const t = (key, replacements) => getTranslation(language, key, replacements);

    const getTitle = () => {
        switch (photoOrder) {
            case 1: return t('photo.title_before');
            case 2: return t('photo.title_middle');
            case 3: return t('photo.title_end');
            default: return t('photo.title_before');
        }
    };

    const getSubtitle = () => {
        switch (photoOrder) {
            case 1: return t('photo.subtitle_before');
            case 2: return t('photo.subtitle_middle');
            case 3: return t('photo.subtitle_end');
            default: return t('photo.subtitle_before');
        }
    };

    // ⭐ NEW: Check permission state on mount (using Permissions API where available)
    useEffect(() => {
        checkCameraPermission();
    }, []);

    useEffect(() => {
        // Only start the camera once we know the permission state
        if (permissionChecked && permissionState !== 'denied' && permissionState !== 'unavailable') {
            startCamera();
        }
        return () => {
            stopCamera();
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, [permissionChecked]);

    const checkCameraPermission = async () => {
        try {
            // Permissions API — supported in Chrome, Edge, Firefox, Safari 16+
            if (navigator.permissions && navigator.permissions.query) {
                const result = await navigator.permissions.query({ name: 'camera' });
                console.log('📸 Camera permission state:', result.state);
                setPermissionState(result.state);

                // Listen for changes (user might grant later)
                result.onchange = () => {
                    console.log('📸 Camera permission changed to:', result.state);
                    setPermissionState(result.state);
                    if (result.state === 'granted') {
                        setError(null);
                        startCamera();
                    }
                };
            } else {
                // Permissions API not supported — assume prompt
                setPermissionState('prompt');
            }
        } catch (err) {
            // Some browsers (older Safari) don't support 'camera' permission query
            console.warn('Permissions API unavailable:', err.message);
            setPermissionState('prompt');
        } finally {
            setPermissionChecked(true);
        }
    };

    const startCamera = async () => {
        try {
            setLoading(true);
            setError(null);

            // Check if mediaDevices is available (requires HTTPS or localhost)
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setPermissionState('unavailable');
                setError(t('photo.camera_not_supported') ||
                    'Camera not supported. Please use a modern browser over HTTPS.');
                setLoading(false);
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'user',
                    width: { ideal: 640 },
                    height: { ideal: 480 }
                },
                audio: false
            });

            streamRef.current = stream;
            setPermissionState('granted');

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play();
                    setCameraReady(true);
                    setLoading(false);
                };
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            handleCameraError(err);
            setLoading(false);
        }
    };

    // ⭐ NEW: Map DOMException names to actionable user messages
    const handleCameraError = (err) => {
        const name = err.name || '';
        const message = err.message || '';

        console.log('📸 Camera error details:', { name, message });

        if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || message.includes('Permission denied')) {
            setPermissionState('denied');
            setError(t('photo.camera_permission_denied') ||
                'Camera access was denied. Please enable camera permission in your browser settings and try again.');
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
            setPermissionState('unavailable');
            setError(t('photo.camera_not_found') ||
                'No camera found on this device. Please connect a camera and try again.');
        } else if (name === 'NotReadableError' || name === 'TrackStartError') {
            setError(t('photo.camera_in_use') ||
                'Camera is already in use by another application. Please close other apps using the camera and try again.');
        } else if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
            setError(t('photo.camera_constraints') ||
                'Camera does not meet the required constraints. Please try a different device.');
        } else if (name === 'SecurityError') {
            setPermissionState('unavailable');
            setError(t('photo.camera_insecure') ||
                'Camera requires a secure connection (HTTPS). Please access this page over HTTPS.');
        } else {
            setError(t('photo.camera_error') ||
                'Unable to access camera. Please allow camera access and try again.');
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setCameraReady(false);
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
    };

    const startCountdown = () => {
        setIsCountingDown(true);
        setCountdown(3);
        let count = 3;

        countdownIntervalRef.current = setInterval(() => {
            count -= 1;
            setCountdown(count);

            if (count <= 0) {
                clearInterval(countdownIntervalRef.current);
                setIsCountingDown(false);
                capturePhoto();
            }
        }, 1000);
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;

        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = canvas.toDataURL('image/jpeg', 0.9);
        setImageSrc(imageData);
        setCaptured(true);

        stopCamera();
        submitPhoto(imageData);
    };

    const submitPhoto = async (imageData) => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.post(`/face-photos/${accessToken}/capture`, {
                photo_order: photoOrder,
                image: imageData,
                background: false
            });

            if (response.data.success) {
                showSuccess(`✅ ` + t('photo.captured_success'));

                onCaptureComplete({
                    photoOrder: response.data.photo_order,
                    photoUrl: response.data.photo_url
                });
            } else {
                setError(response.data.error || t('photo.capture_failed'));
                setCaptured(false);
            }
        } catch (err) {
            console.error('Error submitting photo:', err);
            setError(err.response?.data?.error || t('photo.capture_failed'));
            setCaptured(false);
        } finally {
            setLoading(false);
        }
    };

    const handleRetake = () => {
        setCaptured(false);
        setImageSrc(null);
        setError(null);
        // Re-check permission before restarting
        checkCameraPermission();
        if (permissionState !== 'denied') {
            startCamera();
        }
    };

    const handleTakePhoto = () => {
        if (!cameraReady) {
            setError(t('photo.camera_not_ready'));
            return;
        }
        startCountdown();
    };

    // ⭐ NEW: Retry button re-checks permission first
    const handleRetryCamera = async () => {
        setError(null);
        await checkCameraPermission();
        // Small delay to let permission state update
        setTimeout(() => {
            if (permissionState !== 'denied') {
                startCamera();
            }
        }, 100);
    };

    const getPhotoLabel = () => {
        switch (photoOrder) {
            case 1: return '1st Photo (Before Assessment)';
            case 2: return '2nd Photo (Middle of Assessment)';
            case 3: return '3rd Photo (End of Assessment)';
            default: return `Photo ${photoOrder}`;
        }
    };

    // ⭐ NEW: Full-screen permission blocked UI
    const renderPermissionDenied = () => (
        <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <ExclamationIcon className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
                {t('photo.permission_blocked_title') || 'Camera Access Blocked'}
            </h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                {t('photo.permission_blocked_message') ||
                    'You have denied camera access. To continue with this verification step, please enable camera permission in your browser settings.'}
            </p>

            <div className="text-left bg-gray-50 rounded-lg p-4 max-w-md mx-auto mb-4">
                <p className="text-xs font-bold text-gray-700 mb-2">
                    {t('photo.how_to_enable') || 'How to enable:'}
                </p>
                <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                    <li>{t('photo.enable_step_1') || 'Click the lock/settings icon in your browser\'s address bar'}</li>
                    <li>{t('photo.enable_step_2') || 'Set Camera permission to "Allow"'}</li>
                    <li>{t('photo.enable_step_3') || 'Refresh this page to continue'}</li>
                </ol>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-2.5 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] flex items-center justify-center shadow-sm"
                >
                    <RefreshIcon className="h-4 w-4 mr-2" />
                    {t('photo.refresh_page') || 'Refresh Page'}
                </button>
                <button
                    onClick={handleRetryCamera}
                    className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center justify-center"
                >
                    {t('photo.try_again') || 'Try Again'}
                </button>
            </div>
        </div>
    );

    // ⭐ NEW: Camera unsupported UI (HTTPS/browser issue)
    const renderCameraUnavailable = () => (
        <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <ExclamationIcon className="h-8 w-8 text-orange-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
                {t('photo.camera_unavailable_title') || 'Camera Unavailable'}
            </h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                {t('photo.camera_unavailable_message') ||
                    'This device or browser does not support camera access. Please try a different device or a modern browser.'}
            </p>
        </div>
    );

    // Show dedicated UI when permission is denied or camera unavailable
    if (permissionState === 'denied' && !captured) {
        return (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-[#063b56] px-6 py-4">
                    <h2 className="text-xl font-bold text-white">{getTitle()}</h2>
                    <p className="text-gray-300 text-sm">{getSubtitle()}</p>
                </div>
                {renderPermissionDenied()}
            </div>
        );
    }

    if (permissionState === 'unavailable' && !captured) {
        return (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-[#063b56] px-6 py-4">
                    <h2 className="text-xl font-bold text-white">{getTitle()}</h2>
                    <p className="text-gray-300 text-sm">{getSubtitle()}</p>
                </div>
                {renderCameraUnavailable()}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-[#063b56] px-6 py-4">
                <h2 className="text-xl font-bold text-white"> {getTitle()}</h2>
                <p className="text-gray-300 text-sm"> {getSubtitle()}</p>
            </div>

            <div className="p-6">
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {/* Camera View */}
                <div className="relative bg-black rounded-lg overflow-hidden">
                    {!captured ? (
                        <>
                            <video
                                ref={videoRef}
                                className="w-full h-auto max-h-[400px] object-cover"
                                autoPlay
                                playsInline
                                muted
                            />
                            {!cameraReady && !loading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-800/50">
                                    <div className="text-center text-white">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                                        <p>{t('photo.loading_camera')}</p>
                                    </div>
                                </div>
                            )}

                            {/* Countdown Overlay */}
                            {isCountingDown && countdown > 0 && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                    <div className="text-center">
                                        <div className="w-24 h-24 rounded-full border-4 border-[#ff9f2f] flex items-center justify-center mx-auto animate-pulse">
                                            <span className="text-5xl font-bold text-white">{countdown}</span>
                                        </div>
                                        <p className="text-white text-sm mt-2">{t('photo.smile')}</p>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="relative">
                            <img
                                src={imageSrc}
                                alt="Captured"
                                className="w-full h-auto max-h-[400px] object-cover"
                            />
                            <div className="absolute top-4 right-4">
                                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center">
                                    <CheckIcon className="h-4 w-4 mr-1" />
                                    {t('photo.captured_badge')}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Hidden Canvas for Capture */}
                <canvas ref={canvasRef} className="hidden" />

                {/* Action Buttons */}
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                    {!captured ? (
                        <>
                            <button
                                onClick={handleTakePhoto}
                                disabled={!cameraReady || loading || isCountingDown}
                                className="px-6 py-3 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] disabled:opacity-50 flex items-center shadow-sm"
                            >
                                <CameraIcon className="h-5 w-5 mr-2" />
                                {isCountingDown
                                    ? t('photo.taking_photo')
                                    : t('photo.take_photo')}
                            </button>
                            <button
                                onClick={handleRetryCamera}
                                disabled={cameraReady || loading}
                                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center"
                            >
                                <RefreshIcon className="h-5 w-5 mr-2" />
                                {t('photo.retry_camera')}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={handleRetake}
                                disabled={loading}
                                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center"
                            >
                                <RefreshIcon className="h-5 w-5 mr-2" />
                                {t('photo.retake_photo')}
                            </button>
                            <button
                                onClick={() => submitPhoto(imageSrc)}
                                disabled={loading}
                                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center shadow-sm"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                        {t('photo.saving')}
                                    </>
                                ) : (
                                    <>
                                        <CheckIcon className="h-5 w-5 mr-2" />
                                        {t('photo.save_photo')}
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>

                {/* Instructions */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-600">
                        <strong>{t('photo.tips_title')}</strong>
                        <br />
                        • {t('photo.tip_face_visible')}
                        <br />
                        • {t('photo.tip_look_camera')}
                        <br />
                        • {t('photo.tip_countdown')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ManualFaceCapture;